import { initAudio, getAudioContext, getWaveformData, getAudioFrame } from './audio/analyzer';
import { detectPitch } from './audio/pitch';
import { detectVibrato, clearVibratoHistory, snapToScale } from './audio/musical';
import { SynthEngine, getPlaybackDurationSeconds } from './audio/synth';
import {
  initRenderer, startRenderLoop, setNotes, setPlaybackGlow,
  getParticleSystem, getCanvasForExport, getTransform, setTransform,
  initParticles, notesToSerialized, setAudioAmplitude, getClickSparks,
  getShockwaves, setSymmetryMode, getSymmetryMode, setHoveredNote,
} from './visual/renderer';
import { createNote } from './visual/palette';
import { getStyleForEmotion } from './visual/styleParams';
import { initControls, loadSettings } from './ui/controls';
import { exportPNG } from './export';
import { exportGif } from './ui/gifExport';
import { exportMIDI } from './midiExport';
import { saveCreation, getSavedCreations, deleteCreation, renameCreation } from './archive';
import { copyShareURL, decodeNotesFromURL } from './share';
import { UndoManager } from './undo';
import { setTheme, getCurrentTheme } from './themes';
import { loadEmotionClassifier, classifyEmotion, feedAudioFrame, classifyEmotionFromProsody, getEmotionLoadError, isEmotionReady, isEmotionLoading } from './ai/emotion';
import type { AppMode, UserSettings, HumNote, SerializedNote, ThemeName, CanvasTransform } from './types';

let mode: AppMode = 'intro';
let settings: UserSettings;
let synth: SynthEngine;
let allNotes: HumNote[] = [];
let humStartTime = 0;
let lastFreq = 0;
let noteIdCounter = 0;
let recordingStartTime = 0;
let currentEmotion: string | undefined;
let lastVibrato = false;
let isDemoMode = false;
const undoManager = new UndoManager();

const introScreen = document.getElementById('intro-screen')!;
const errorScreen = document.getElementById('error-screen')!;
const micPulse = document.getElementById('mic-pulse')!;
const freqDisplay = document.getElementById('freq-display')!;
const freqValue = document.getElementById('freq-value')!;
const statusBar = document.getElementById('status-bar')!;
const noteCountEl = document.getElementById('note-count')!;
const timerEl = document.getElementById('recording-timer')!;
const playbackControls = document.getElementById('playback-controls')!;
const startBtn = document.getElementById('start-btn') as HTMLButtonElement;
const retryBtn = document.getElementById('retry-btn') as HTMLButtonElement;
const playBtn = document.getElementById('play-btn') as HTMLButtonElement;
const stopBtn = document.getElementById('stop-btn') as HTMLButtonElement;
const clearBtn = document.getElementById('clear-btn') as HTMLButtonElement;
const exportBtn = document.getElementById('export-png');
const exportGifBtn = document.getElementById('export-gif');
const shareBtn = document.getElementById('share-btn') as HTMLButtonElement;
const archiveToggle = document.getElementById('archive-toggle') as HTMLButtonElement;
const archivePanel = document.getElementById('archive-panel')!;
const archiveList = document.getElementById('archive-list')!;
const saveBtn = document.getElementById('save-btn') as HTMLButtonElement;
const resumeBtn = document.getElementById('resume-btn') as HTMLButtonElement;
const resumeDivider = document.querySelector('.resume-divider') as HTMLElement;
const settingsPanel = document.getElementById('settings-panel')!;
const toolbar = document.getElementById('toolbar')!;
const themeBar = document.getElementById('theme-bar')!;
const zoomIndicator = document.getElementById('zoom-indicator')!;
const zoomValue = document.getElementById('zoom-value')!;
const themeSwatches = document.querySelectorAll('.theme-swatch') as NodeListOf<HTMLButtonElement>;
const toast = document.getElementById('toast')!;
const miniWaveformCanvas = document.getElementById('mini-waveform') as HTMLCanvasElement;
const miniWaveCtx = miniWaveformCanvas?.getContext('2d');
const floatingPlayBtn = document.getElementById('floating-play-btn') as HTMLButtonElement;
const emotionBadge = document.getElementById('emotion-badge')!;
const emotionLabel = document.getElementById('emotion-label')!;
const emotionIcon = document.getElementById('emotion-icon')!;
const modelLoadingOverlay = document.getElementById('model-loading-overlay')!;
const modelLoadingBar = document.getElementById('model-loading-bar')!;
const modelLoadingText = document.getElementById('model-loading-text')!;
const timelineBar = document.getElementById('timeline-bar')!;
const timelineProgress = document.getElementById('timeline-progress')!;
const timelinePlayhead = document.getElementById('timeline-playhead')!;
const timelineTime = document.getElementById('timeline-time')!;
const timelineDuration = document.getElementById('timeline-duration')!;
const vibratoIndicator = document.getElementById('vibrato-indicator')!;
const homeBtn = document.getElementById('home-btn') as HTMLButtonElement;

function showToast(msg: string, duration = 2000): void {
  toast.textContent = msg;
  toast.classList.remove('hidden');
  setTimeout(() => toast.classList.add('hidden'), duration);
}

function updateEmotionBadge(emotion: string | undefined): void {
  if (!emotionBadge || !emotionLabel || !emotionIcon) return;
  if (!emotion) {
    emotionBadge.classList.add('hidden');
    return;
  }
  emotionLabel.textContent = emotion;
  const icons: Record<string, string> = {
    happy: '☀', sad: '💧', angry: '⚡', fear: '👁', neutral: '◯', surprise: '✦', disgust: '〰',
  };
  emotionIcon.textContent = icons[emotion] || '◯';
  emotionBadge.classList.remove('hidden');
}

function animateTitle(): void {
  const title = document.getElementById('animated-title')!;
  const text = 'Cosmic Hum';
  title.innerHTML = '';
  text.split('').forEach((char, i) => {
    const span = document.createElement('span');
    span.className = char === ' ' ? 'title-char space' : 'title-char';
    span.textContent = char === ' ' ? '\u00A0' : char;
    span.style.animationDelay = `${0.1 + i * 0.05}s`;
    title.appendChild(span);
  });
}

function initBackgroundCanvas(): void {
  const bgCanvas = document.getElementById('bg-canvas') as HTMLCanvasElement;
  if (!bgCanvas) return;
  const bgCtx = bgCanvas.getContext('2d')!;
  const dpr = window.devicePixelRatio || 1;

  function resize() {
    bgCanvas.width = window.innerWidth * dpr;
    bgCanvas.height = window.innerHeight * dpr;
    bgCanvas.style.width = window.innerWidth + 'px';
    bgCanvas.style.height = window.innerHeight + 'px';
    bgCtx.scale(dpr, dpr);
  }
  resize();
  window.addEventListener('resize', resize);

  let t = 0;
  const pianoKeys = 24;
  const particles: { x: number; y: number; vx: number; vy: number; size: number; alpha: number; hue: number }[] = [];
  for (let i = 0; i < 40; i++) {
    particles.push({
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      vx: (Math.random() - 0.5) * 0.3,
      vy: -Math.random() * 0.4 - 0.1,
      size: Math.random() * 2.5 + 0.5,
      alpha: Math.random() * 0.3 + 0.05,
      hue: Math.random() * 60 + 260,
    });
  }

  function render() {
    t += 0.004;
    const w = window.innerWidth;
    const h = window.innerHeight;
    bgCtx.save();
    bgCtx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const grad = bgCtx.createLinearGradient(
      w * 0.5 + Math.sin(t) * w * 0.3, 0,
      w * 0.5 + Math.cos(t * 0.7) * w * 0.3, h,
    );
    grad.addColorStop(0, '#08080f');
    grad.addColorStop(0.3, 'rgba(15, 5, 30, 1)');
    grad.addColorStop(0.6, 'rgba(5, 10, 25, 1)');
    grad.addColorStop(1, '#08080f');
    bgCtx.fillStyle = grad;
    bgCtx.fillRect(0, 0, w, h);

    bgCtx.globalAlpha = 0.12 + Math.sin(t * 0.5) * 0.04;
    const wash = bgCtx.createRadialGradient(
      w * 0.5 + Math.cos(t * 0.3) * w * 0.25,
      h * 0.5 + Math.sin(t * 0.4) * h * 0.25,
      0, w * 0.5, h * 0.5, w * 0.6,
    );
    wash.addColorStop(0, 'rgba(100, 150, 255, 0.4)');
    wash.addColorStop(0.5, 'rgba(200, 100, 255, 0.2)');
    wash.addColorStop(1, 'transparent');
    bgCtx.fillStyle = wash;
    bgCtx.fillRect(0, 0, w, h);
    bgCtx.globalAlpha = 1;

    // Aurora waves
    for (let layer = 0; layer < 3; layer++) {
      bgCtx.beginPath();
      const layerY = h * 0.25 + layer * h * 0.12;
      bgCtx.moveTo(0, layerY);
      for (let x = 0; x <= w; x += 4) {
        const y = layerY
          + Math.sin(x * 0.003 + t * (0.8 + layer * 0.3)) * 30
          + Math.sin(x * 0.007 + t * 1.2 + layer) * 15
          + Math.cos(x * 0.001 + t * 0.5) * 20;
        bgCtx.lineTo(x, y);
      }
      bgCtx.lineTo(w, h);
      bgCtx.lineTo(0, h);
      bgCtx.closePath();
      const hue = (260 + layer * 40 + t * 20) % 360;
      bgCtx.fillStyle = `hsla(${hue}, 60%, 30%, 0.04)`;
      bgCtx.fill();
    }

    // Piano keys silhouette
    const keyW = w / pianoKeys;
    const keyH = h * 0.08;
    const keyY = h - keyH;
    const blackKeyPattern = [1, 1, 0, 1, 1, 1, 0];
    for (let i = 0; i < pianoKeys; i++) {
      const x = i * keyW;
      const isBlack = blackKeyPattern[i % 7];
      const wave = Math.sin(t * 2 + i * 0.5) * 0.5 + 0.5;
      if (isBlack) {
        bgCtx.fillStyle = `rgba(20, 18, 30, ${0.5 + wave * 0.3})`;
        bgCtx.fillRect(x + keyW * 0.6, keyY, keyW * 0.4, keyH * 0.6);
      } else {
        bgCtx.fillStyle = `rgba(25, 22, 38, ${0.4 + wave * 0.2})`;
        bgCtx.fillRect(x, keyY, keyW - 1, keyH);
        bgCtx.strokeStyle = `rgba(100, 80, 160, ${0.1 + wave * 0.15})`;
        bgCtx.lineWidth = 0.5;
        bgCtx.strokeRect(x, keyY, keyW - 1, keyH);
      }
      if (wave > 0.8) {
        bgCtx.shadowColor = `hsla(${260 + i * 15}, 80%, 60%, 0.3)`;
        bgCtx.shadowBlur = 8;
        bgCtx.fillStyle = `hsla(${260 + i * 15}, 80%, 60%, ${(wave - 0.8) * 0.5})`;
        bgCtx.fillRect(x, keyY, keyW - 1, keyH);
        bgCtx.shadowBlur = 0;
      }
    }

    // Floating particles
    for (const p of particles) {
      p.x += p.vx;
      p.y += p.vy;
      if (p.y < -10) { p.y = h + 10; p.x = Math.random() * w; }
      if (p.x < -10) p.x = w + 10;
      if (p.x > w + 10) p.x = -10;
      bgCtx.beginPath();
      bgCtx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
      bgCtx.fillStyle = `hsla(${p.hue}, 60%, 70%, ${p.alpha})`;
      bgCtx.fill();
    }

    bgCtx.restore();
    requestAnimationFrame(render);
  }
  render();
}

function setMode(newMode: AppMode): void {
  mode = newMode;
  introScreen.classList.toggle('hidden', newMode !== 'intro');
  micPulse.classList.toggle('hidden', newMode !== 'recording');
  freqDisplay.classList.toggle('hidden', newMode !== 'recording');
  statusBar.classList.toggle('hidden', newMode === 'intro');
  playbackControls.classList.toggle('hidden', newMode === 'intro' || newMode === 'recording');
  toolbar.classList.toggle('hidden', newMode === 'intro');
  themeBar.classList.toggle('hidden', newMode === 'intro');
  zoomIndicator.classList.toggle('hidden', newMode === 'intro');
  if (homeBtn) {
    homeBtn.classList.toggle('hidden', newMode === 'intro');
  }
  if (vibratoIndicator && newMode !== 'recording') {
    vibratoIndicator.classList.remove('active');
  }
  if (newMode !== 'recording' && newMode !== 'intro') {
    timelineBar.classList.add('hidden');
  }
  updateButtonStates();
}

function updateButtonStates(): void {
  const hasNotes = allNotes.length > 0;
  const isPlaying = synth && synth.playing;
  playBtn.disabled = !hasNotes;
  stopBtn.disabled = !isPlaying;
  playBtn.innerHTML = isPlaying
    ? '<span class="play-icon">&#10074;&#10074;</span> Pause'
    : '<span class="play-icon">&#9654;</span> Play';

  const showResume = hasNotes && !isPlaying && mode === 'idle' && !isDemoMode;
  resumeBtn.classList.toggle('hidden', !showResume);
  resumeDivider.classList.toggle('hidden', !showResume);

  if (floatingPlayBtn) {
    floatingPlayBtn.classList.toggle('hidden', mode === 'intro');
    floatingPlayBtn.classList.toggle('playing', isPlaying);
  }
}

function updateStats(): void {
  const count = allNotes.length;
  noteCountEl.textContent = `${count} note${count !== 1 ? 's' : ''}`;
  const elapsed = Math.floor((Date.now() - recordingStartTime) / 1000);
  const min = Math.floor(elapsed / 60);
  const sec = elapsed % 60;
  timerEl.textContent = `${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

function formatTimelineTime(ms: number): string {
  const sec = Math.floor(ms / 1000);
  const min = Math.floor(sec / 60);
  const s = sec % 60;
  return `${min}:${String(s).padStart(2, '0')}`;
}

let lastSpawnFrame = 0;
const SPAWN_INTERVAL_FRAMES = 16;
const MAX_NOTES = 500;
let recentNotePositions: { x: number; y: number; color: string; size: number; shape: import('./types').ShapeType; rotation: number }[] = [];

function drawMiniWaveform(
  ctx2: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  waveData: Float32Array | null,
  theme: import('./types').ThemePreset,
): void {
  const w = canvas.width;
  const h = canvas.height;
  ctx2.clearRect(0, 0, w, h);
  if (!waveData || waveData.length === 0) return;

  const bins = Math.min(waveData.length, 60);
  const step = Math.floor(waveData.length / bins);
  const barW = w / bins;

  ctx2.fillStyle = theme.accent;
  ctx2.globalAlpha = 0.7;
  for (let i = 0; i < bins; i++) {
    const val = Math.abs(waveData[i * step] || 0);
    const barH = Math.max(1, val * h * 0.9);
    ctx2.fillRect(i * barW, (h - barH) / 2, barW - 1, barH);
  }
  ctx2.globalAlpha = 1;
}

async function recordingTick(): Promise<void> {
  if (mode !== 'recording') return;

  updateStats();

  const pitch = detectPitch();
  if (!pitch) {
    if (humStartTime > 0) {
      humStartTime = 0;
      lastFreq = 0;
      freqValue.textContent = '---';
      freqValue.style.color = '';
      recentNotePositions = [];
      lastVibrato = false;
      if (vibratoIndicator) vibratoIndicator.classList.remove('active');
    }
    if (miniWaveCtx) {
      miniWaveCtx.clearRect(0, 0, miniWaveformCanvas.width, miniWaveformCanvas.height);
    }
    requestAnimationFrame(recordingTick);
    return;
  }

  const hasPitch = pitch.frequency > 0 && pitch.clarity > 0.75 && pitch.amplitude > 0.02;

  setAudioAmplitude(hasPitch ? Math.min(1, pitch.amplitude * 5) : 0);

  if (!hasPitch) {
    freqValue.textContent = '---';
    freqValue.style.color = '';
    if (humStartTime > 0) {
      humStartTime = 0;
      lastFreq = 0;
      recentNotePositions = [];
      lastVibrato = false;
      if (vibratoIndicator) vibratoIndicator.classList.remove('active');
    }
    if (miniWaveCtx) {
      miniWaveCtx.clearRect(0, 0, miniWaveformCanvas.width, miniWaveformCanvas.height);
    }
    requestAnimationFrame(recordingTick);
    return;
  }

  if (hasPitch) {
    const displayFreq = Math.round(pitch.frequency);
    freqValue.textContent = String(displayFreq);
    freqValue.style.color = getCurrentTheme().accent;

    const particles = getParticleSystem();
    if (particles) particles.pulseAmplitude(pitch.amplitude);

    if (humStartTime === 0) {
      humStartTime = performance.now();
      clearVibratoHistory();
    }

      const quantized = snapToScale(pitch.frequency, settings?.scale || 'major');
      lastFreq = quantized;

      const vibrato = detectVibrato(quantized);
      lastVibrato = vibrato.hasVibrato;

      if (vibratoIndicator) {
        vibratoIndicator.classList.toggle('active', vibrato.hasVibrato);
      }

    if (miniWaveCtx) {
      const waveData = getWaveformData();
      drawMiniWaveform(miniWaveCtx, miniWaveformCanvas, waveData, getCurrentTheme());
    }

    lastSpawnFrame++;
    if (lastSpawnFrame >= SPAWN_INTERVAL_FRAMES) {
      lastSpawnFrame = 0;
      const w = window.innerWidth;
      const h = window.innerHeight;
      const theme = getCurrentTheme();
      const transform = getTransform();
      const amp = Math.min(1, pitch.amplitude * 5);

      const frame = getAudioFrame();
      if (frame) {
        feedAudioFrame(frame);
        let emotionResult = await classifyEmotion();
        if (!emotionResult) {
          emotionResult = classifyEmotionFromProsody(lastFreq, amp, vibrato.depth);
        }
        if (emotionResult) {
          currentEmotion = emotionResult.label;
          updateEmotionBadge(currentEmotion);
        }
      }

      const currentStyle = getStyleForEmotion(currentEmotion);
      if (lastVibrato) {
        currentStyle.trailLength = Math.min(30, currentStyle.trailLength + 8);
        currentStyle.glowMultiplier = Math.min(3, currentStyle.glowMultiplier + 0.5);
        currentStyle.particleBurstCount = Math.min(12, currentStyle.particleBurstCount + 3);
      }
       const holdDuration = humStartTime > 0 ? performance.now() - humStartTime : 350;
       const note = createNote(
         lastFreq, amp, performance.now(), holdDuration,
         w, h, transform, theme, noteIdCounter++, currentEmotion, currentStyle,
       );
      note.trail = recentNotePositions.map(p => ({ x: p.x, y: p.y }));
      recentNotePositions.push({ x: note.x, y: note.y, color: note.color, size: note.size, shape: note.shape, rotation: note.rotation });
      if (recentNotePositions.length > 12) recentNotePositions.shift();
      allNotes.push(note);
      if (allNotes.length > MAX_NOTES) {
        allNotes = allNotes.slice(allNotes.length - MAX_NOTES);
      }
      setNotes(allNotes);

      const ps = getParticleSystem();
      if (ps) {
        const screenBX = note.x * transform.scale + transform.x;
        const screenBY = note.y * transform.scale + transform.y;
        const burstCount = note.style ? note.style.particleBurstCount : 4;
        ps.burst(screenBX, screenBY, note.color, burstCount);
      }

      const sparks = getClickSparks();
      if (sparks) {
        const screenBX = note.x * transform.scale + transform.x;
        const screenBY = note.y * transform.scale + transform.y;
        sparks.emit(screenBX, screenBY, note.color, 3);
      }

      const shockwaves = getShockwaves();
      if (shockwaves) {
        const screenBX = note.x * transform.scale + transform.x;
        const screenBY = note.y * transform.scale + transform.y;
        shockwaves.emit(screenBX, screenBY, note.color, 120);
      }
      updateStats();
    }
  }

  requestAnimationFrame(recordingTick);
}

let playbackMode: 'realtime' | 'condensed' = 'realtime';
let currentPlaybackIndex = 0;
let timelineIntervalId: any = null;

function startPlayback(startIndex = 0): void {
  if (allNotes.length === 0 || !synth) return;

  setMode('playback');
  synth.loadNotes(allNotes);
  synth.setTimbre(settings.waveType);
  synth.setVolume(settings.volume);

  timelineBar.classList.remove('hidden');
  const totalDurSec = getPlaybackDurationSeconds(allNotes, playbackMode === 'condensed');
  timelineDuration.textContent = formatTimelineTime(totalDurSec * 1000);

  const audioCtx = getAudioContext();
  const startAudioTime = audioCtx ? audioCtx.currentTime : 0;

  if (timelineIntervalId) clearInterval(timelineIntervalId);
  timelineIntervalId = setInterval(() => {
    if (!synth || !synth.playing) {
      clearInterval(timelineIntervalId);
      return;
    }
    const currentAudioTime = audioCtx ? audioCtx.currentTime : startAudioTime;
    const elapsedSec = Math.max(0, currentAudioTime - startAudioTime);
    const pct = Math.min(100, totalDurSec > 0 ? (elapsedSec / totalDurSec) * 100 : 0);

    timelineProgress.style.width = `${pct}%`;
    timelinePlayhead.style.left = `${pct}%`;
    timelineTime.textContent = formatTimelineTime(elapsedSec * 1000);
  }, 40);

  synth.play((index) => {
    currentPlaybackIndex = index;
    setPlaybackGlow(index);
    updateButtonStates();
  }, startIndex, playbackMode === 'condensed').then(() => {
    if (timelineIntervalId) clearInterval(timelineIntervalId);
    timelineProgress.style.width = '100%';
    timelinePlayhead.style.left = '100%';
    timelineTime.textContent = formatTimelineTime(totalDurSec * 1000);
    setTimeout(() => timelineBar.classList.add('hidden'), 800);
    setPlaybackGlow(-1);
    currentPlaybackIndex = 0;
    setMode('idle');
    updateButtonStates();
  });

  updateButtonStates();
}

function stopPlayback(): void {
  if (synth) synth.stop();
  if (timelineIntervalId) clearInterval(timelineIntervalId);
  setPlaybackGlow(-1);
  timelineBar.classList.add('hidden');
  updateButtonStates();
}

async function start(): Promise<void> {
  isDemoMode = false;
  try {
    await initAudio();
  } catch {
    introScreen.classList.add('hidden');
    errorScreen.classList.remove('hidden');
    return;
  }

  settings = loadSettings();
  const audioCtx = getAudioContext();
  if (audioCtx) {
    synth = new SynthEngine(audioCtx);
    synth.setTimbre(settings.waveType);
    synth.setVolume(settings.volume);
  }

  if (!isEmotionReady() && !isEmotionLoading()) {
    modelLoadingOverlay.classList.remove('hidden');
    loadEmotionClassifier((progress, loaded, total) => {
      const pct = Math.min(100, Math.round(progress));
      modelLoadingBar.style.width = `${pct}%`;
      const mbLoaded = (loaded / (1024 * 1024)).toFixed(0);
      const mbTotal = (total / (1024 * 1024)).toFixed(0);
      modelLoadingText.textContent = `Loading AI brain... ${mbLoaded}/${mbTotal} MB`;
    }).then(() => {
      modelLoadingOverlay.classList.add('hidden');
      if (isEmotionReady()) {
        showToast('AI emotion detection ready!');
      } else {
        const err = getEmotionLoadError();
        console.warn('[HumCanvas] Emotion model failed:', err);
        showToast('Running heuristic emotion mode');
      }
    });
  }

  const canvas = document.getElementById('art-canvas') as HTMLCanvasElement;
  initRenderer(canvas);
  initParticles();
  initControls((s) => {
    settings = s;
    if (synth) {
      synth.setTimbre(s.waveType);
      synth.setVolume(s.volume);
    }
  }, (waveType) => {
    if (synth && audioCtx) {
      synth.setTimbre(waveType);
      synth.playPreview(440, 0.3);
    }
  });

  settingsPanel.classList.remove('hidden');
  setTimeout(() => settingsPanel.classList.add('hidden'), 2500);

  const settingsToggle = document.getElementById('settings-toggle')!;
  settingsToggle.addEventListener('click', () => {
    archivePanel.classList.add('hidden');
    archiveToggle.classList.remove('active');
  });

  const sharedNotes = await decodeNotesFromURL();
  if (sharedNotes && sharedNotes.length > 0) {
    restoreSerializedNotes(sharedNotes);
    showToast(`Loaded ${sharedNotes.length} shared notes`);
  }

  startRenderLoop();
  recordingStartTime = Date.now();
  setMode('recording');
  recordingTick();
}

function restoreSerializedNotes(serialized: SerializedNote[]): void {
  const theme = getCurrentTheme();
  allNotes = serialized.map((s) => ({
    ...s,
    id: noteIdCounter++,
    x: s.x,
    y: s.y,
    size: s.size,
    vx: 0,
    vy: 0,
    rotation: s.rotation || Math.random() * Math.PI * 2,
    rotationSpeed: s.style ? s.style.rotationSpeed : 0,
    alpha: theme.shapeAlpha * (s.style ? s.style.alphaMultiplier : 1.0),
    birthTime: performance.now(),
    trail: [],
    emotion: s.emotion,
    style: s.style,
  }));
  setNotes(allNotes);
}

function renderArchiveList(): void {
  const creations = getSavedCreations();
  archiveList.innerHTML = '';
  if (creations.length === 0) {
    archiveList.innerHTML = '<p class="archive-empty">No saved creations yet</p>';
    return;
  }
  for (const c of creations.reverse()) {
    const item = document.createElement('div');
    item.className = 'archive-item';
    const date = new Date(c.createdAt).toLocaleDateString();
    const dur = `${Math.floor(c.duration / 1000)}s`;
    item.innerHTML = `
      <img class="archive-thumb" src="${c.thumbnail}" alt="${c.name}" />
      <div class="archive-info">
        <span class="archive-name">${c.name}</span>
        <span class="archive-meta">${date} &middot; ${dur} &middot; ${c.notes.length} notes</span>
      </div>
      <div class="archive-actions">
        <button class="archive-load" data-id="${c.id}" title="Load">&#9654;</button>
        <button class="archive-rename" data-id="${c.id}" title="Rename">&#9998;</button>
        <button class="archive-delete" data-id="${c.id}" title="Delete">&#10005;</button>
      </div>
    `;
    archiveList.appendChild(item);
  }

  archiveList.querySelectorAll('.archive-load').forEach(btn => {
    btn.addEventListener('click', () => {
      const id = (btn as HTMLElement).dataset.id!;
      const c = getSavedCreations().find(cr => cr.id === id);
      if (c) {
        stopPlayback();
        restoreSerializedNotes(c.notes);
        showToast(`Loaded "${c.name}"`);
        archivePanel.classList.add('hidden');
      }
    });
  });

  archiveList.querySelectorAll('.archive-delete').forEach(btn => {
    btn.addEventListener('click', () => {
      const id = (btn as HTMLElement).dataset.id!;
      deleteCreation(id);
      renderArchiveList();
      showToast('Deleted');
    });
  });

  archiveList.querySelectorAll('.archive-rename').forEach(btn => {
    btn.addEventListener('click', () => {
      const id = (btn as HTMLElement).dataset.id!;
      const newName = prompt('Rename creation:');
      if (newName && newName.trim()) {
        renameCreation(id, newName.trim());
        renderArchiveList();
      }
    });
  });
}

async function loadDemoMasterpiece(songNum = 1): Promise<void> {
  const audioCtx = getAudioContext() || new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
  if (audioCtx.state === 'suspended') {
    await audioCtx.resume();
  }
  synth = new SynthEngine(audioCtx);
  settings = loadSettings();
  synth.setTimbre(settings.waveType);
  synth.setVolume(settings.volume);

  const canvas = document.getElementById('art-canvas') as HTMLCanvasElement;
  initRenderer(canvas);
  initParticles();
  initControls((s) => {
    settings = s;
    if (synth) {
      synth.setTimbre(s.waveType);
      synth.setVolume(s.volume);
    }
  }, (waveType) => {
    if (synth && audioCtx) {
      synth.setTimbre(waveType);
      synth.playPreview(440, 0.3);
    }
  });

  const theme = getCurrentTheme();
  const songs: Record<number, { note: number; em: string; dur?: number }[]> = {
    1: [ // Ode to Joy (Full Theme)
      { note: 329.63, em: 'happy' }, { note: 329.63, em: 'happy' }, { note: 349.23, em: 'happy' }, { note: 392.00, em: 'surprise' },
      { note: 392.00, em: 'happy' }, { note: 349.23, em: 'happy' }, { note: 329.63, em: 'happy' }, { note: 293.66, em: 'neutral' },
      { note: 261.63, em: 'happy' }, { note: 261.63, em: 'happy' }, { note: 293.66, em: 'happy' }, { note: 329.63, em: 'happy' },
      { note: 329.63, em: 'happy' }, { note: 293.66, em: 'neutral' }, { note: 293.66, em: 'neutral' },
      // second phrase
      { note: 329.63, em: 'happy' }, { note: 329.63, em: 'happy' }, { note: 349.23, em: 'happy' }, { note: 392.00, em: 'surprise' },
      { note: 392.00, em: 'happy' }, { note: 349.23, em: 'happy' }, { note: 329.63, em: 'happy' }, { note: 293.66, em: 'neutral' },
      { note: 261.63, em: 'happy' }, { note: 261.63, em: 'happy' }, { note: 293.66, em: 'happy' }, { note: 329.63, em: 'happy' },
      { note: 293.66, em: 'neutral' }, { note: 261.63, em: 'happy' }, { note: 261.63, em: 'happy' }
    ],
    2: [ // Twinkle Twinkle Little Star (Full Song)
      { note: 261.63, em: 'happy' }, { note: 261.63, em: 'happy' }, { note: 392.00, em: 'surprise' }, { note: 392.00, em: 'happy' },
      { note: 440.00, em: 'happy' }, { note: 440.00, em: 'happy' }, { note: 392.00, em: 'surprise' },
      { note: 349.23, em: 'neutral' }, { note: 349.23, em: 'neutral' }, { note: 329.63, em: 'happy' }, { note: 329.63, em: 'happy' },
      { note: 293.66, em: 'neutral' }, { note: 293.66, em: 'neutral' }, { note: 261.63, em: 'happy' },
      // middle lines
      { note: 392.00, em: 'surprise' }, { note: 392.00, em: 'happy' }, { note: 349.23, em: 'neutral' }, { note: 349.23, em: 'neutral' },
      { note: 329.63, em: 'happy' }, { note: 329.63, em: 'happy' }, { note: 293.66, em: 'neutral' },
      { note: 392.00, em: 'surprise' }, { note: 392.00, em: 'happy' }, { note: 349.23, em: 'neutral' }, { note: 349.23, em: 'neutral' },
      { note: 329.63, em: 'happy' }, { note: 329.63, em: 'happy' }, { note: 293.66, em: 'neutral' },
      // final refrain
      { note: 261.63, em: 'happy' }, { note: 261.63, em: 'happy' }, { note: 392.00, em: 'surprise' }, { note: 392.00, em: 'happy' },
      { note: 440.00, em: 'happy' }, { note: 440.00, em: 'happy' }, { note: 392.00, em: 'surprise' },
      { note: 349.23, em: 'neutral' }, { note: 349.23, em: 'neutral' }, { note: 329.63, em: 'happy' }, { note: 329.63, em: 'happy' },
      { note: 293.66, em: 'neutral' }, { note: 293.66, em: 'neutral' }, { note: 261.63, em: 'happy' }
    ],
    3: [ // Tetris Theme (Korobeiniki - Full Arrangement)
      { note: 329.63, em: 'angry' }, { note: 246.94, em: 'neutral' }, { note: 277.18, em: 'happy' }, { note: 293.66, em: 'surprise' },
      { note: 277.18, em: 'happy' }, { note: 246.94, em: 'neutral' }, { note: 220.00, em: 'sad' }, { note: 220.00, em: 'sad' },
      { note: 277.18, em: 'happy' }, { note: 329.63, em: 'angry' }, { note: 293.66, em: 'surprise' }, { note: 277.18, em: 'happy' },
      { note: 246.94, em: 'neutral' }, { note: 277.18, em: 'happy' }, { note: 293.66, em: 'surprise' }, { note: 329.63, em: 'angry' },
      { note: 293.66, em: 'surprise' }, { note: 277.18, em: 'happy' }, { note: 246.94, em: 'neutral' }, { note: 246.94, em: 'neutral' },
      { note: 277.18, em: 'happy' }, { note: 293.66, em: 'surprise' }, { note: 329.63, em: 'angry' }, { note: 277.18, em: 'happy' },
      { note: 220.00, em: 'sad' }, { note: 220.00, em: 'sad' }, { note: 220.00, em: 'sad' }
    ],
    4: [ // Happy Birthday (Full Song)
      { note: 261.63, em: 'happy' }, { note: 261.63, em: 'happy' }, { note: 293.66, em: 'surprise' }, { note: 261.63, em: 'happy' },
      { note: 349.23, em: 'happy' }, { note: 329.63, em: 'neutral' },
      { note: 261.63, em: 'happy' }, { note: 261.63, em: 'happy' }, { note: 293.66, em: 'surprise' }, { note: 261.63, em: 'happy' },
      { note: 392.00, em: 'surprise' }, { note: 349.23, em: 'happy' },
      { note: 261.63, em: 'happy' }, { note: 261.63, em: 'happy' }, { note: 523.25, em: 'happy' }, { note: 440.00, em: 'happy' },
      { note: 349.23, em: 'happy' }, { note: 329.63, em: 'neutral' }, { note: 293.66, em: 'surprise' },
      { note: 466.16, em: 'happy' }, { note: 466.16, em: 'happy' }, { note: 440.00, em: 'happy' },
      { note: 349.23, em: 'happy' }, { note: 392.00, em: 'surprise' }, { note: 349.23, em: 'happy' }
    ]
  };

  const selectedMelody = songs[songNum] || songs[1];

  const w = window.innerWidth;
  const h = window.innerHeight;
  const dummyTransform: CanvasTransform = { x: 0, y: 0, scale: 1.0 };
  allNotes = [];

  selectedMelody.forEach((m, idx) => {
    const style = getStyleForEmotion(m.em);
    const note = createNote(
      m.note, 0.85, idx * 300, 280,
      w, h, dummyTransform, theme, idx, m.em, style,
    );
    allNotes.push(note);
  });

  isDemoMode = true;
  setNotes(allNotes);
  startRenderLoop();
  setMode('idle');
  showToast(`Loaded Song #${songNum}! Click ▶ Play`);
}

const loadDemoBtn = document.getElementById('load-demo-btn');
const demoSelect = document.getElementById('demo-select') as HTMLSelectElement;
if (loadDemoBtn && demoSelect) {
  loadDemoBtn.addEventListener('click', () => {
    const val = Number(demoSelect.value) || 1;
    loadDemoMasterpiece(val);
  });
}
animateTitle();
initBackgroundCanvas();

startBtn.addEventListener('click', start);
retryBtn.addEventListener('click', start);

playBtn.addEventListener('click', () => {
  if (synth && synth.playing) {
    stopPlayback();
    setMode('idle');
    showToast('Paused');
  } else if (allNotes.length > 0) {
    startPlayback(currentPlaybackIndex);
  }
});

floatingPlayBtn.addEventListener('click', () => {
  if (synth && synth.playing) {
    stopPlayback();
    setMode('idle');
    showToast('Paused');
  } else if (allNotes.length > 0) {
    startPlayback(currentPlaybackIndex);
  } else {
    showToast('Hum something first');
  }
});

const playbackModeBtn = document.getElementById('playback-mode-btn') as HTMLButtonElement;
if (playbackModeBtn) {
  playbackModeBtn.addEventListener('click', () => {
    playbackMode = playbackMode === 'realtime' ? 'condensed' : 'realtime';
    playbackModeBtn.textContent = `Rhythm: ${playbackMode === 'realtime' ? 'Real-Time' : 'Condensed (0.5s)'}`;
    showToast(`Playback mode: ${playbackMode === 'realtime' ? 'Real-Time' : 'Condensed'}`);
  });
}

const timelineTrack = document.getElementById('timeline-track');
if (timelineTrack) {
  timelineTrack.addEventListener('click', (e) => {
    if (allNotes.length === 0) return;
    const rect = timelineTrack.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const ratio = Math.max(0, Math.min(1, clickX / rect.width));
    const targetIndex = Math.floor(ratio * allNotes.length);

    if (synth && synth.playing) {
      synth.stop();
    }
    startPlayback(targetIndex);
    showToast(`Scrubbed to note ${targetIndex + 1}/${allNotes.length}`);
  });
}

stopBtn.addEventListener('click', () => {
  stopPlayback();
  setMode('idle');
});

clearBtn.addEventListener('click', async () => {
  stopPlayback();
  isDemoMode = false;
  undoManager.push(notesToSerialized(allNotes));
  allNotes = [];
  setNotes(allNotes);
  setPlaybackGlow(-1);
  updateStats();
  try {
    await initAudio();
  } catch {
    // ignore
  }
  setMode('recording');
  recordingStartTime = Date.now();
  recordingTick();
});

resumeBtn.addEventListener('click', () => {
  stopPlayback();
  setMode('recording');
  recordingStartTime = Date.now();
  recordingTick();
});

if (exportGifBtn) {
  exportGifBtn.addEventListener('click', () => {
    if (allNotes.length === 0) {
      showToast('Nothing to export yet');
      return;
    }
    const dur = allNotes.length > 0
      ? allNotes[allNotes.length - 1].timestamp - allNotes[0].timestamp + allNotes[allNotes.length - 1].duration
      : 5000;
    exportGif(Math.min(dur, 10000));
  });
}

if (exportBtn) {
  exportBtn.addEventListener('click', () => {
    exportPNG(getCanvasForExport());
  });
}

if (homeBtn) {
  homeBtn.addEventListener('click', () => {
    stopPlayback();
    setMode('intro');
    showToast('Returned to Title');
  });
}

const exportMidiBtn = document.getElementById('export-midi');
if (exportMidiBtn) {
  exportMidiBtn.addEventListener('click', () => {
    if (allNotes.length === 0) {
      showToast('Nothing to export yet');
      return;
    }
    exportMIDI(notesToSerialized(allNotes));
    showToast('MIDI melody exported!');
  });
}

const settingsClose = document.getElementById('settings-close');
if (settingsClose) {
  settingsClose.addEventListener('click', () => {
    settingsPanel.classList.add('hidden');
  });
}

const archiveClose = document.getElementById('archive-close');
if (archiveClose) {
  archiveClose.addEventListener('click', () => {
    archivePanel.classList.add('hidden');
    archiveToggle.classList.remove('active');
  });
}

shareBtn.addEventListener('click', async () => {
  if (allNotes.length === 0) {
    showToast('Nothing to share yet');
    return;
  }
  await copyShareURL(notesToSerialized(allNotes));
  showToast('Share URL copied!');
});

const symmetryToggle = document.getElementById('symmetry-toggle') as HTMLButtonElement;
const symmetryLabel = document.getElementById('symmetry-label')!;
if (symmetryToggle) {
  symmetryToggle.addEventListener('click', () => {
    const current = getSymmetryMode();
    const next: 1 | 2 | 4 = current === 1 ? 2 : (current === 2 ? 4 : 1);
    setSymmetryMode(next);
    if (symmetryLabel) symmetryLabel.textContent = `${next}x`;
    showToast(`Symmetry mode: ${next}x`);
  });
}
archiveToggle.addEventListener('click', () => {
  const isHidden = archivePanel.classList.contains('hidden');
  archivePanel.classList.toggle('hidden');
  archiveToggle.classList.toggle('active', isHidden);
  if (isHidden) {
    settingsPanel.classList.add('hidden');
    renderArchiveList();
  }
});

saveBtn.addEventListener('click', () => {
  if (allNotes.length === 0) {
    showToast('Nothing to save yet');
    return;
  }
  const name = prompt('Name your creation:', `Creation ${getSavedCreations().length + 1}`);
  if (!name) return;
  const srcCanvas = getCanvasForExport();
  const thumbCanvas = document.createElement('canvas');
  thumbCanvas.width = 120;
  thumbCanvas.height = 80;
  const thumbCtx = thumbCanvas.getContext('2d')!;
  thumbCtx.drawImage(srcCanvas, 0, 0, 120, 80);
  const thumbnail = thumbCanvas.toDataURL('image/jpeg', 0.4);
  const duration = allNotes.length > 0
    ? allNotes[allNotes.length - 1].timestamp - allNotes[0].timestamp + allNotes[allNotes.length - 1].duration
    : 0;
  const saved = saveCreation(name, notesToSerialized(allNotes), thumbnail, duration);
  if (saved) {
    showToast(`Saved "${name}"`);
  } else {
    showToast('Save failed (storage full)');
  }
});

themeSwatches.forEach(swatch => {
  swatch.addEventListener('click', () => {
    const themeName = swatch.dataset.theme as ThemeName;
    setTheme(themeName);
    themeSwatches.forEach(s => s.classList.remove('active'));
    swatch.classList.add('active');
  });
});

// Zoom / Pan
let isPanning = false;
let panStartX = 0;
let panStartY = 0;
let panStartTX = 0;
let panStartTY = 0;
let currentTransform: CanvasTransform = { x: 0, y: 0, scale: 1 };
let mouseDownX = 0;
let mouseDownY = 0;
let mouseDownTime = 0;

function clampTransform() {
  const w = window.innerWidth;
  const h = window.innerHeight;
  currentTransform.scale = Math.max(1.0, Math.min(3.5, currentTransform.scale));
  const maxPanX = (currentTransform.scale - 1) * (w / 2);
  const maxPanY = (currentTransform.scale - 1) * (h / 2);
  currentTransform.x = Math.max(-maxPanX, Math.min(maxPanX, currentTransform.x));
  currentTransform.y = Math.max(-maxPanY, Math.min(maxPanY, currentTransform.y));
}

function updateTransform() {
  clampTransform();
  setTransform(currentTransform);
  zoomValue.textContent = `${currentTransform.scale.toFixed(1)}x`;
}

const artCanvas = document.getElementById('art-canvas') as HTMLCanvasElement;

artCanvas.addEventListener('wheel', (e) => {
  if (mode === 'intro') return;
  e.preventDefault();
  const rect = artCanvas.getBoundingClientRect();
  const mouseX = e.clientX - rect.left;
  const mouseY = e.clientY - rect.top;
  const oldScale = currentTransform.scale;
  const delta = e.deltaY > 0 ? -0.1 : 0.1;
  const newScale = Math.max(0.25, Math.min(4, oldScale + delta));
  const worldX = (mouseX - currentTransform.x) / oldScale;
  const worldY = (mouseY - currentTransform.y) / oldScale;
  currentTransform.x = mouseX - worldX * newScale;
  currentTransform.y = mouseY - worldY * newScale;
  currentTransform.scale = newScale;
  updateTransform();
}, { passive: false });

artCanvas.addEventListener('mousedown', (e) => {
  if (mode === 'intro') return;
  mouseDownX = e.clientX;
  mouseDownY = e.clientY;
  mouseDownTime = performance.now();
  isPanning = true;
  panStartX = e.clientX;
  panStartY = e.clientY;
  panStartTX = currentTransform.x;
  panStartTY = currentTransform.y;
  artCanvas.style.cursor = 'grabbing';
});

window.addEventListener('mousemove', (e) => {
  if (!isPanning) {
    if (mode !== 'intro') {
      const rect = artCanvas.getBoundingClientRect();
      const screenX = e.clientX - rect.left;
      const screenY = e.clientY - rect.top;
      const worldX = (screenX - currentTransform.x) / currentTransform.scale;
      const worldY = (screenY - currentTransform.y) / currentTransform.scale;

      let found: HumNote | null = null;
      for (let i = allNotes.length - 1; i >= 0; i--) {
        const n = allNotes[i];
        const distSq = (n.x - worldX) ** 2 + (n.y - worldY) ** 2;
        if (distSq < (n.size * 0.9) ** 2) {
          found = n;
          break;
        }
      }
      setHoveredNote(found);
      artCanvas.style.cursor = found ? 'pointer' : 'default';
    }
    return;
  }
  currentTransform.x = panStartTX + (e.clientX - panStartX);
  currentTransform.y = panStartTY + (e.clientY - panStartY);
  updateTransform();
});

document.addEventListener('click', (e) => {
  const target = (e.target as HTMLElement).closest('button, .theme-swatch, select');
  if (target && synth) {
    synth.playUIClick();
  }
});

window.addEventListener('mouseup', () => {
  isPanning = false;
  artCanvas.style.cursor = '';
});

artCanvas.addEventListener('dblclick', () => {
  currentTransform = { x: 0, y: 0, scale: 1 };
  updateTransform();
  showToast('View reset');
});

artCanvas.addEventListener('click', (e) => {
  if (mode === 'intro' || mode === 'playback') return;
  const dx = e.clientX - mouseDownX;
  const dy = e.clientY - mouseDownY;
  const dist = Math.sqrt(dx * dx + dy * dy);
  const elapsed = performance.now() - mouseDownTime;
  if (dist > 8 || elapsed > 300) return;
  if (allNotes.length === 0) return;

  const rect = artCanvas.getBoundingClientRect();
  const screenX = e.clientX - rect.left;
  const screenY = e.clientY - rect.top;
  const worldX = (screenX - currentTransform.x) / currentTransform.scale;
  const worldY = (screenY - currentTransform.y) / currentTransform.scale;

  let nearestIdx = 0;
  let nearestDist = Infinity;
  for (let i = 0; i < allNotes.length; i++) {
    const n = allNotes[i];
    const d = (n.x - worldX) ** 2 + (n.y - worldY) ** 2;
    if (d < nearestDist) {
      nearestDist = d;
      nearestIdx = i;
    }
  }

  const hitNote = allNotes[nearestIdx];
  const hitRadius = hitNote.size * 1.5;
  if (Math.sqrt(nearestDist) > hitRadius) {
    startPlayback();
    return;
  }

  setMode('playback');
  synth.loadNotes(allNotes);
  synth.setTimbre(settings.waveType);
  synth.setVolume(settings.volume);
  synth.play((index) => {
    setPlaybackGlow(index);
    updateButtonStates();
  }, nearestIdx).then(() => {
    setPlaybackGlow(-1);
    setMode('idle');
    updateButtonStates();
  });
  updateButtonStates();
});

let lastTouchDist = 0;
let lastTouchX = 0;
let lastTouchY = 0;

artCanvas.addEventListener('touchstart', (e) => {
  if (mode === 'intro') return;
  if (e.touches.length === 2) {
    const dx = e.touches[0].clientX - e.touches[1].clientX;
    const dy = e.touches[0].clientY - e.touches[1].clientY;
    lastTouchDist = Math.sqrt(dx * dx + dy * dy);
  } else if (e.touches.length === 1) {
    isPanning = true;
    lastTouchX = e.touches[0].clientX;
    lastTouchY = e.touches[0].clientY;
    panStartTX = currentTransform.x;
    panStartTY = currentTransform.y;
  }
}, { passive: true });

artCanvas.addEventListener('touchmove', (e) => {
  if (mode === 'intro') return;
  if (e.touches.length === 2) {
    e.preventDefault();
    const dx = e.touches[0].clientX - e.touches[1].clientX;
    const dy = e.touches[0].clientY - e.touches[1].clientY;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const midX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
    const midY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
    if (lastTouchDist > 0) {
      const oldScale = currentTransform.scale;
      const scaleDelta = dist / lastTouchDist;
      const newScale = Math.max(0.25, Math.min(4, oldScale * scaleDelta));
      const worldX = (midX - currentTransform.x) / oldScale;
      const worldY = (midY - currentTransform.y) / oldScale;
      currentTransform.x = midX - worldX * newScale;
      currentTransform.y = midY - worldY * newScale;
      currentTransform.scale = newScale;
      updateTransform();
    }
    lastTouchDist = dist;
  } else if (e.touches.length === 1 && isPanning) {
    currentTransform.x = panStartTX + (e.touches[0].clientX - lastTouchX);
    currentTransform.y = panStartTY + (e.touches[0].clientY - lastTouchY);
    updateTransform();
  }
}, { passive: false });

artCanvas.addEventListener('touchend', () => {
  isPanning = false;
  lastTouchDist = 0;
});

document.addEventListener('keydown', (e) => {
  if (mode === 'intro') return;

  if (e.code === 'Space') {
    e.preventDefault();
    if (synth && synth.playing) {
      stopPlayback();
      setMode('idle');
    } else if (allNotes.length > 0) {
      startPlayback();
    }
  }

  if (e.code === 'Escape') {
    if (mode === 'playback') {
      stopPlayback();
      setMode('idle');
    }
    archivePanel.classList.add('hidden');
    archiveToggle.classList.remove('active');
  }

  if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
    e.preventDefault();
    if (e.shiftKey) {
      const restored = undoManager.redo(notesToSerialized(allNotes));
      if (restored) {
        restoreSerializedNotes(restored);
        showToast('Redo');
      }
    } else {
      const restored = undoManager.undo(notesToSerialized(allNotes));
      if (restored) {
        restoreSerializedNotes(restored);
        showToast('Undo');
      }
    }
    updateButtonStates();
  }
});
